/**
 * Multi-tab auth sync (logout + context token updates).
 * Uses BroadcastChannel when available, with localStorage ping fallback.
 */

import { store } from '@/app/store'
import { tokenManager } from '@/utils/tokenManager'
import { setContextToken, resetAuthState } from '@/store/slices/auth.slice'
import { resetPermissions } from '@/store/slices/permissions.slice'
import { resetNavigation } from '@/store/slices/navigation.slice'
import { UrlGenerator } from '@/utils/url-generator.util'

const CHANNEL_NAME = 'vidhyantra-auth'

export type AuthBroadcastMessage =
  | { type: 'logout' }
  | { type: 'context-token-updated'; contextToken: string }

const LS_KEY = '__vidhyantra_auth_bc__'

let channel: BroadcastChannel | null = null
let initialized = false

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
    } catch {
      return null
    }
  }
  return channel
}

function applyContextTokenFromPeer(contextToken: string) {
  tokenManager.setToken('contextToken', contextToken, { expires: 7 })
  store.dispatch(setContextToken(contextToken))
}

function applyLogoutFromPeer() {
  try {
    tokenManager.clearAllAuthData()
    store.dispatch(resetAuthState())
    store.dispatch(resetPermissions())
    store.dispatch(resetNavigation())
    sessionStorage.setItem('logoutInProgress', 'true')
  } catch {
    /* ignore */
  }
  const path = window.location.pathname
  if (!path.includes('/login') && !path.includes('/logout')) {
    window.location.href = UrlGenerator.getBaseLogoutUrl()
  }
}

export function emitAuthBroadcastIfSupported(message: AuthBroadcastMessage) {
  try {
    const ch = getChannel()
    if (ch) {
      ch.postMessage(message)
    }
    const ping = JSON.stringify({ ...message, ts: Date.now() })
    localStorage.setItem(LS_KEY, ping)
    queueMicrotask(() => {
      try {
        localStorage.removeItem(LS_KEY)
      } catch {
        /* ignore */
      }
    })
  } catch {
    /* ignore */
  }
}

export function initAuthBroadcastListeners(): void {
  if (initialized) return
  initialized = true
  if (typeof window === 'undefined') return

  const handler = (payload: AuthBroadcastMessage) => {
    if (payload.type === 'logout') {
      applyLogoutFromPeer()
    }
    if (payload.type === 'context-token-updated' && payload.contextToken) {
      applyContextTokenFromPeer(payload.contextToken)
    }
  }

  try {
    const ch = getChannel()
    ch?.addEventListener('message', (ev: MessageEvent<AuthBroadcastMessage>) => {
      if (ev?.data) handler(ev.data)
    })
  } catch {
    /* ignore */
  }

  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== LS_KEY || !e.newValue) return
    try {
      const data = JSON.parse(e.newValue) as AuthBroadcastMessage
      if (data && 'type' in data) handler(data)
    } catch {
      /* ignore */
    }
  })
}
